import { ChangeEvent, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { countries } from "../../utils/countries";
import { BillingAddress } from "../../services/planChange";
import { usePlanChangeFormat } from "./usePlanChangeFormat";

const FIELD =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

const LABEL = "block text-sm font-medium text-gray-700";

export const isBillingAddressComplete = (address: BillingAddress) =>
  Boolean(address.line1.trim() && address.city.trim() && address.country);

const BillingAddressFields = ({
  value,
  onChange,
  showMissing,
}: {
  value: BillingAddress;
  onChange: (next: BillingAddress) => void;
  showMissing: boolean;
}) => {
  const { i18n } = useTranslation();
  const { k } = usePlanChangeFormat();

  const countryOptions = useMemo(() => {
    let regionNames: Intl.DisplayNames | null = null;
    try {
      regionNames = new Intl.DisplayNames([i18n.language], { type: "region" });
    } catch {
      regionNames = null;
    }
    return countries
      .map((country) => ({
        code: country.code,
        name: regionNames?.of(country.code) || country.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [i18n.language]);

  const set =
    (key: keyof BillingAddress) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onChange({ ...value, [key]: event.target.value });

  const missing = (field: string) =>
    showMissing && !field.trim() ? "border-red-500" : "";

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <label htmlFor="billing-line1" className={LABEL}>
          {k("address.line1")}
        </label>
        <input
          id="billing-line1"
          className={twMerge(FIELD, "mt-1", missing(value.line1))}
          autoComplete="address-line1"
          value={value.line1}
          onChange={set("line1")}
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="billing-line2" className={LABEL}>
          {k("address.line2")}
        </label>
        <input
          id="billing-line2"
          className={twMerge(FIELD, "mt-1")}
          autoComplete="address-line2"
          value={value.line2 ?? ""}
          onChange={set("line2")}
        />
      </div>
      <div>
        <label htmlFor="billing-postal-code" className={LABEL}>
          {k("address.postalCode")}
        </label>
        <input
          id="billing-postal-code"
          className={twMerge(FIELD, "mt-1")}
          autoComplete="postal-code"
          value={value.postal_code ?? ""}
          onChange={set("postal_code")}
        />
      </div>
      <div>
        <label htmlFor="billing-city" className={LABEL}>
          {k("address.city")}
        </label>
        <input
          id="billing-city"
          className={twMerge(FIELD, "mt-1", missing(value.city))}
          autoComplete="address-level2"
          value={value.city}
          onChange={set("city")}
        />
      </div>
      <div>
        <label htmlFor="billing-state" className={LABEL}>
          {k("address.state")}
        </label>
        <input
          id="billing-state"
          className={twMerge(FIELD, "mt-1")}
          autoComplete="address-level1"
          value={value.state ?? ""}
          onChange={set("state")}
        />
      </div>
      <div>
        <label htmlFor="billing-country" className={LABEL}>
          {k("address.country")}
        </label>
        <select
          id="billing-country"
          className={twMerge(FIELD, "mt-1", missing(value.country))}
          autoComplete="country"
          value={value.country}
          onChange={set("country")}
        >
          <option value="" />
          {countryOptions.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default BillingAddressFields;
